import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '@/utils/api';
import { AGENDA_SLOTS } from './agendaUtils';
import { EmployeeSelect } from './EmployeeSelect';

const EMPTY = { date: '', time: '16:30', end_time: '17:00', title: '', assignee: 'Équipe atelier', assignee_employee_id: '', notes: '' };

export const WorkshopTaskDialog = ({ open, task, defaultDate, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [internal, setInternal] = useState([]);
  useEffect(() => { axios.get(`${API}/workshop/catalog`).then(r => setInternal(r.data.internal_motifs || [])).catch(() => {}); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(task ? { ...EMPTY, ...task } : { ...EMPTY, date: defaultDate || new Date().toISOString().split('T')[0] });
  }, [open, task, defaultDate]);

  const save = async () => {
    if (!form.title.trim() || !form.date || !form.time) { toast.error('Date, heure et tâche requises'); return; }
    if (form.end_time && form.end_time <= form.time) { toast.error("L'heure de fin doit être après le début"); return; }
    const payload = { date: form.date, time: form.time, end_time: form.end_time, title: form.title, assignee: form.assignee, assignee_employee_id: form.assignee_employee_id || '', notes: form.notes };
    try {
      if (task) await axios.put(`${API}/agenda-tasks/${task.id}`, payload);
      else await axios.post(`${API}/agenda-tasks`, payload);
      toast.success(task ? 'Tâche modifiée' : 'Tâche ajoutée');
      onSaved();
    } catch { toast.error('Erreur lors de la sauvegarde'); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try { await axios.delete(`${API}/agenda-tasks/${task.id}`); toast.success('Tâche supprimée'); onSaved(); }
    catch { toast.error('Erreur'); }
  };

  const slotSelect = (key, testid) => (
    <Select value={form[key] || ''} onValueChange={v => set(key, v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="--:--" /></SelectTrigger>
      <SelectContent>{AGENDA_SLOTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="workshop-task-dialog">
        <DialogHeader><DialogTitle>{task ? 'Modifier la tâche atelier' : 'Nouvelle tâche atelier'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Tâche</Label><Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Ex : Nettoyage atelier" data-testid="task-title-input" />
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="task-quick-titles">
              {internal.filter(m => m.key !== 'int_autre').map(m => (
                <button key={m.key} type="button" onClick={() => set('title', m.label)} data-testid={`task-quick-${m.key}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.title === m.label ? 'bg-[#0E1116] text-[#FACC15] border-[#0E1116]' : 'bg-white border-slate-300 hover:border-slate-500'}`}>{m.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => set('date', e.target.value)} data-testid="task-date-input" /></div>
            <div><Label>Début</Label>{slotSelect('time', 'task-start-select')}</div>
            <div><Label>Fin</Label>{slotSelect('end_time', 'task-end-select')}</div>
          </div>
          <div><Label>Qui ?</Label><EmployeeSelect value={form.assignee_employee_id} onChange={(id, name) => setForm(f => ({ ...f, assignee_employee_id: id, assignee: name }))} testid="task-assignee-select" /></div>
          <div><Label>Remarques</Label><Textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} data-testid="task-notes-input" /></div>
          <div className="flex gap-2 pt-2">
            {task && <Button variant="outline" className="text-red-600" onClick={remove} data-testid="task-delete-btn"><Trash2 className="w-4 h-4 mr-1" />Supprimer</Button>}
            <Button className="ml-auto btn-primary" onClick={save} data-testid="task-save-btn">Enregistrer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
